import type {ReactElement} from 'react';
import {getSocialPlatformString, reelOptionsIcons} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getDefaultDisplayURL, isFacebookAccount, isInstagramAccount, isTikTokAccount, isYoutubeAccount} from '@Components/social-media/account.vo';
import {Text, TextSize} from '@Components/text';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {Button, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {MediaTypePublishingParams, PlatformMediaType, SocialMediaAccountId} from '@Components/social-media/post.vo';
import styles from './social-post-content-reel-bottom-panel.module.scss';

export interface SocialPostContentReelBottomPanelProps {
  account: SocialAccount;
  postPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  platformMediaType: PlatformMediaType;
}

export function SocialPostContentReelBottomPanel({account, postPublishingParams, platformMediaType}: SocialPostContentReelBottomPanelProps): ReactElement<any> {
  const accountId = account.id;
  const publishingParams = postPublishingParams[accountId];
  const getReelCaptionStyles = (): string => {
    if (isYoutubeAccount(account)) {
      return `${styles.reelCaptionContainer} _full-width _fit-height flex-v-row`;
    }

    if (isTikTokAccount(account)) {
      return `${styles.reelCaptionContainer} _full-width _fit-height flex-v-row`;
    }

    return `${styles.reelCaptionContainer} _full-width _fit-height`;
  };

  const renderReelOptionsIcons = (): ReactElement<any>[] => {
    const optionIcons = reelOptionsIcons[getSocialPlatformString(account)];
    const iconContainer = [];
    for (let i = 0; i < optionIcons.length; i++) {
      iconContainer.push(
        <Icon
          key={i}
          className={`${isYoutubeAccount(account) ? 'spacing-m-t-2' : `${styles.instagramIcons}`}`}
          type={isYoutubeAccount(account) ? IconType.FLAT : IconType.DARK_GHOST}
          icon={`${optionIcons[i]}`}
          shape={IconShape.CIRCLE}
          size={isYoutubeAccount(account) ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
        />
      );
    }

    return iconContainer;
  };

  const getCaptionFromParams = (): string => {
    const activeParams = publishingParams[platformMediaType];

    if (!activeParams) {
      return '';
    }

    if ('caption' in activeParams && activeParams.caption !== '') {
      return activeParams.caption;
    }

    return '';
  };

  const getReelProfileInformationByPlatform = (): ReactElement<any> | null => {
    if (isInstagramAccount(account)) {
      return (
        <>
          {renderReelOptionsIcons()}
          <img
            alt={window.i18next.t('pmwjs_account_display_alt')}
            className={`spacing-m-t-2 ${styles.instaProfileImage}`}
            src={account?.displayURL ? account?.displayURL : getDefaultDisplayURL()}
          />
        </>
      );
    }

    if (isTikTokAccount(account)) {
      return (
        <>
          {account !== undefined ? <SocialProfileIcon account={account} size={SocialProfileIconSize.LARGE} showPlatformIcon={false} /> : null}
          <div className={styles.tiktokIconDiv}>
            <img alt="tiktok heart icon" aria-hidden className="_fit-height" src={window.PMW.util.asset_url('images/socialmedia/heart-icon-tiktok.svg')} />
          </div>
          <div className={styles.tiktokIconDiv}>
            <img alt="tiktok message icon" aria-hidden className="_fit-height" src={window.PMW.util.asset_url('images/socialmedia/message-icon-tiktok.svg')} />
          </div>
          <div className={styles.tiktokIconDiv}>
            <img alt="tiktok save icon" aria-hidden className={`${styles.tiktokIcon} _fit-height`} src={window.PMW.util.asset_url('images/socialmedia/bookmark-icon-tiktok.svg')} />
          </div>
          <div className={styles.tiktokIconDiv}>
            <img
              alt="tiktok share icon"
              aria-hidden
              className={`${styles.tiktokIcon} _fit-height`}
              src={window.PMW.util.asset_url('images/socialmedia/share-external-icon-tiktok.svg')}
            />
          </div>
        </>
      );
    }

    if (isYoutubeAccount(account)) {
      return (
        <div className={`${styles.youtubeReelProfileInformation} _full-width`}>
          <div className={styles.youtubeReelSocialCredentials}>
            {account !== undefined ? <SocialProfileIcon account={account} size={SocialProfileIconSize.MEDIUM} showPlatformIcon={false} /> : null}
            <Text className="spacing-m-l-2 content-body-white" size={TextSize.XSMALL} bold val={`${account?.name}`} />
          </div>
          <Button customClasses={styles.youtubeSubscribeButton} type={Type.GHOST} size={Size.SMALL} text={`${window.i18next.t('pmwjs_subscribe')}`} />
        </div>
      );
    }

    return null;
  };

  const getReelCaptionText = (): ReactElement<any> => {
    const textClasses = `content-body-white ${styles.overflowingCaptions}`;

    if (isTikTokAccount(account)) {
      return (
        <>
          <Text className="content-body-white" size={TextSize.SMALL} bold val={`${account?.name}`} />
          <Text className={textClasses} size={TextSize.XSMALL} bold val={`${window.i18next.t('pmwjs_reel_placeholder_tiktok')}`} />
        </>
      );
    }

    return (
      <>
        <Text className={textClasses} size={TextSize.XSMALL} bold val={`${getCaptionFromParams()}`} />
        {isYoutubeAccount(account) ? getReelProfileInformationByPlatform() : null}
      </>
    );
  };

  const renderReelLeftPanel = (): ReactElement<any> => {
    return (
      <>
        <div className={`${getReelCaptionStyles()}`}>{getReelCaptionText()}</div>
        {!isYoutubeAccount(account) ? (
          <div
            className={`${isYoutubeAccount(account) ? `${styles.youtubeReelProfileInformation} _full-width` : styles.instagramReelProfileInfo} ${
              isTikTokAccount(account) ? styles.tiktokProfile : ''
            }`}
          >
            {getReelProfileInformationByPlatform()}
          </div>
        ) : null}
      </>
    );
  };

  const getClassesForBottomReel = (): string => {
    if (isFacebookAccount(account)) {
      return `${styles.bottomReel} _fit-height ${styles.facebookReelGradient}`;
    }

    if (isInstagramAccount(account)) {
      return `${styles.bottomReel} _fit-height ${styles.instagramReelGradient}`;
    }

    return `${styles.bottomReel} _fit-height`;
  };

  return <div className={`${getClassesForBottomReel()} ${styles.reelCaptionContainer} _full-width _fit-height`}>{renderReelLeftPanel()}</div>;
}
